import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AssetItem {
  type: string;
  amount: number;
}

export interface LiabilityItem {
  type: string;
  amount: number;
}

export interface BalanceSheetData {
  assets: {
    items: AssetItem[];
    totalAssets: number;
  };
  liabilities: {
    items: LiabilityItem[];
    totalLiabilities: number;
  };
  equity: {
    totalEquity: number;
  };
}

export const useRealBalanceSheet = (companyId: string | undefined, startDate: string, endDate: string) => {
  return useQuery({
    queryKey: ['real-balance-sheet', companyId, startDate, endDate],
    queryFn: async () => {
      if (!companyId) return null;

      const [bankStatementsResult, balanceSheetItemsResult, bankAccountsResult] = await Promise.all([
        supabase
          .from('bank_statements')
          .select('*')
          .eq('company_id', companyId)
          .gte('transaction_date', startDate)
          .lte('transaction_date', endDate),
        supabase
          .from('balance_sheet_items')
          .select('*')
          .eq('company_id', companyId)
          .gte('date', startDate)
          .lte('date', endDate),
        supabase
          .from('bank_accounts')
          .select('*')
          .eq('company_id', companyId)
          .eq('is_active', true),
      ]);

      const bankStatements = bankStatementsResult.data;
      const balanceSheetItems = balanceSheetItemsResult.data;
      const bankAccounts = bankAccountsResult.data;

      if (!bankStatements && !balanceSheetItems && !bankAccounts) return null;

      // --- Assets ---
      const assetsByType = new Map<string, number>();

      // Point-in-time: use the latest entry per type from bank_statements
      const assetTransactions = bankStatements?.filter(
        stmt => stmt.category?.toLowerCase() === 'balance sheet' && 
                stmt.bucket?.toLowerCase() === 'assets'
      ) || [];
      const latestAssetByType = new Map<string, { amount: number; date: string }>();
      assetTransactions.forEach(stmt => {
        const type = stmt.type || 'Other Assets';
        const existing = latestAssetByType.get(type);
        const txDate = stmt.transaction_date || '';
        if (!existing || txDate > existing.date) {
          latestAssetByType.set(type, { amount: Number(stmt.amount), date: txDate });
        }
      });
      latestAssetByType.forEach(({ amount }, type) => {
        assetsByType.set(type, (assetsByType.get(type) || 0) + amount);
      });

      // Point-in-time: use the latest entry per type from balance_sheet_items
      const manualAssetItems = balanceSheetItems?.filter(
        item => item.subsection?.toLowerCase() === 'assets'
      ) || [];
      const latestManualAssetByType = new Map<string, { amount: number; date: string }>();
      manualAssetItems.forEach(item => {
        const type = item.item_type || 'Other Assets';
        const existing = latestManualAssetByType.get(type);
        const itemDate = item.date || '';
        if (!existing || itemDate > existing.date) {
          latestManualAssetByType.set(type, { amount: Number(item.amount), date: itemDate });
        }
      });
      latestManualAssetByType.forEach(({ amount }, type) => {
        assetsByType.set(type, (assetsByType.get(type) || 0) + amount);
      });

      // Add bank account cash balances as a current asset
      // current_balance is persisted by Teller edge functions but may not be in generated types
      const totalBankCash = (bankAccounts || []).reduce(
        (sum, acc) => sum + (Number((acc as Record<string, unknown>).current_balance) || 0), 0
      );
      if (totalBankCash > 0) {
        assetsByType.set('Cash and Bank Accounts', (assetsByType.get('Cash and Bank Accounts') || 0) + totalBankCash);
      }

      const assetItems: AssetItem[] = Array.from(assetsByType.entries()).map(([type, amount]) => ({
        type,
        amount
      }));

      const totalAssets = assetItems.reduce((sum, item) => sum + item.amount, 0);

      // --- Liabilities ---
      const liabilitiesByType = new Map<string, number>();

      // Point-in-time: use the latest entry per type from bank_statements
      const liabilityTransactions = bankStatements?.filter(
        stmt => stmt.category?.toLowerCase() === 'balance sheet' && 
                (stmt.bucket?.toLowerCase() === 'debt service' || 
                 stmt.bucket?.toLowerCase() === 'liabilities')
      ) || [];
      const latestLiabilityByType = new Map<string, { amount: number; date: string }>();
      liabilityTransactions.forEach(stmt => {
        const type = stmt.type || 'Other Liabilities';
        const existing = latestLiabilityByType.get(type);
        const txDate = stmt.transaction_date || '';
        if (!existing || txDate > existing.date) {
          latestLiabilityByType.set(type, { amount: Math.abs(Number(stmt.amount)), date: txDate });
        }
      });
      latestLiabilityByType.forEach(({ amount }, type) => {
        liabilitiesByType.set(type, (liabilitiesByType.get(type) || 0) + amount);
      });

      // Point-in-time: use the latest entry per type from balance_sheet_items
      const manualLiabilityItems = balanceSheetItems?.filter(
        item => item.subsection?.toLowerCase() === 'liabilities'
      ) || [];
      const latestManualLiabilityByType = new Map<string, { amount: number; date: string }>();
      manualLiabilityItems.forEach(item => {
        const type = item.item_type || 'Other Liabilities';
        const existing = latestManualLiabilityByType.get(type);
        const itemDate = item.date || '';
        if (!existing || itemDate > existing.date) {
          latestManualLiabilityByType.set(type, { amount: Number(item.amount), date: itemDate });
        }
      });
      latestManualLiabilityByType.forEach(({ amount }, type) => {
        liabilitiesByType.set(type, (liabilitiesByType.get(type) || 0) + amount);
      });

      const liabilityItems: LiabilityItem[] = Array.from(liabilitiesByType.entries()).map(([type, amount]) => ({
        type,
        amount
      }));

      const totalLiabilities = liabilityItems.reduce((sum, item) => sum + item.amount, 0);

      const totalEquity = totalAssets - totalLiabilities;

      const balanceSheetData: BalanceSheetData = {
        assets: {
          items: assetItems,
          totalAssets
        },
        liabilities: {
          items: liabilityItems,
          totalLiabilities
        },
        equity: {
          totalEquity
        }
      };

      return balanceSheetData;
    },
    enabled: !!companyId
  });
};
